import {
  addToCartAction,
  createEmptyCart,
  getCart,
  mergeCartGuestToUser,
  removeCartItem,
  updateCart,
  updateCartAll,
} from '@/features/cart'
import { applyCoupon, removeCoupon as removeCouponService } from '@/features/coupon/services'
import { getCookieToken } from '@/libs/cookies'
import { toast } from 'react-toastify'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { useWishlistStore } from './wishlist'

export enum TYPE_CART {
  GUEST = 'guest',
  USER = 'user',
}

// ==================== TYPES ====================
type CartState = {
  cartId: string
  cartItems: IListCartItems[] | []
  cartType: TYPE_CART.GUEST | TYPE_CART.USER
  cartData: ICart
  isLoading: boolean
  pendingOperations: number // Đếm số lượng operations đang chạy
  shippingFee: number
  cashbackCoin: number
  showCartMini: boolean // State để control việc hiển thị cart mini
  isAutoCloseMiniCart: boolean // State để control auto-close cart mini sau 3s
}
type CartAction = {
  refetchCart: () => Promise<boolean | undefined>
  // creatCartId: (cookies?: string | null) => Promise<void>
  clearCart: () => void
  mergeCart: () => Promise<any>
  removeCartItem: (cartItemUID: string, isRefetch?: boolean) => Promise<void>
  updateCartItem: (cartItemUID: string, quantity: string, available_to_checkout: boolean) => Promise<void>
  addCoupon: (coupon: string) => Promise<void>
  removeCoupon: () => Promise<void>
  swapCoupon: (newCoupon: string) => Promise<void> // NEW FUNCTION
  addToCart: (items: ICartItem[], isToast?: boolean) => Promise<void | string>
  replaceCartId: (cartId: string) => Promise<void>
  uncheckAllItem: (check?: boolean) => Promise<void>
  clearUserCart: () => void
  setLoading: (loading: boolean) => void
  setShippingFee: (fee: number) => void
  setShowCartMini: (show: boolean) => void // Action để set state hiển thị cart mini
  setIsAutoCloseMiniCart: (isAuto: boolean) => void // Action để set auto-close
}

type CartStore = CartState & CartAction

// ==================== DEFAULT STATE ====================
const defaultCart: CartState = {
  cartId: '',
  cartItems: [],
  cartType: TYPE_CART.GUEST,
  isLoading: false,
  cartData: {} as ICart,
  pendingOperations: 0,
  shippingFee: 0,
  cashbackCoin: 0,
  showCartMini: false,
  isAutoCloseMiniCart: false,
}

// ==================== HELPER FUNCTIONS ====================

const scheduleRefetch = async (get: () => CartStore) => {
  if (get().pendingOperations === 0) {
    await get().refetchCart()
  }
}

/**
 * Wrapper để tự động xử lý pending operations counter
 * Đảm bảo counter luôn được tăng/giảm đúng và refetch sau khi operation hoàn thành
 */
const withPendingOperation = async <T>(
  get: () => CartStore,
  set: (fn: (state: CartStore) => CartStore) => void,
  operation: () => Promise<T>,
  options: {
    shouldRefetch?: boolean
    setLoading?: boolean
  } = {}
): Promise<T | undefined> => {
  const { shouldRefetch = true, setLoading: shouldSetLoading = true } = options

  // Tăng counter khi bắt đầu operation
  set((state) => ({
    ...state,
    isLoading: shouldSetLoading ? true : state.isLoading,
    pendingOperations: state.pendingOperations + 1,
  }))

  try {
    return await operation()
  } finally {
    // Giảm counter khi hoàn thành (dù thành công hay lỗi)
    set((state) => ({
      ...state,
      isLoading: false,
      pendingOperations: Math.max(0, state.pendingOperations - 1),
    }))

    // Schedule refetch với debounce nếu cần
    if (shouldRefetch) {
      await scheduleRefetch(get)
    }
  }
}

/**
 * Hiển thị thông báo lỗi từ response
 */
const handleErrorResponse = (res: any, defaultMessage?: string): void => {
  if (res?.status === 'error') {
    throw new Error(res?.message || defaultMessage || 'Có lỗi xảy ra')
  }
}

// ==================== STORE IMPLEMENTATION ====================
export const useCartStore = create<CartStore>()(
  devtools(
    persist(
      (set, get) => ({
        cartId: defaultCart.cartId,
        cartItems: defaultCart.cartItems,
        cartType: defaultCart.cartType,
        isLoading: defaultCart.isLoading,
        cartData: defaultCart.cartData,
        pendingOperations: defaultCart.pendingOperations,
        shippingFee: defaultCart.shippingFee,
        cashbackCoin: defaultCart.cashbackCoin,
        showCartMini: defaultCart.showCartMini,
        isAutoCloseMiniCart: defaultCart.isAutoCloseMiniCart,

        // ==================== CART FETCHING ====================
        refetchCart: async () => {
          // Chỉ refetch khi không còn operation nào đang chạy
          if (get().pendingOperations > 0) {
            return
          }

          set((state) => ({ ...state, isLoading: true }))

          // Hàm để tái tạo cart_id khi cần
          const reCreateCartId = async () => {
            const id = await createEmptyCart()
            if (!id) {
              return set((state) => {
                state.cartId = ''
                return state
              })
            }
            const token = await getCookieToken()
            set((state) => {
              state.cartId = id
              if (!!token) {
                state.cartType = TYPE_CART.USER
                get().refetchCart()
                useWishlistStore.getState().getWishlist({})
              }
              return state
            })
          }

          // Nếu không có cart_id, chạy lại logic tạo cart_id
          if (!get().cartId) {
            await reCreateCartId()
          }

          // Lấy thông tin cart
          const items = await getCart(get().cartId).then(async (res) => {
            if (!res) {
              // Nếu không lấy được cart, tạo lại cart_id
              await reCreateCartId()
            }
            return res
          })
          // Tính phí ship: nếu giá trị đơn hàng >= order_amount thì free ship (0), ngược lại lấy shipping_fee
          const shippingFeeValue = items?.shipping_information?.shipping_fee ?? 0
          const isHaveItems = !!items?.items.length
          // tính membership discount
          const amountOfMembershipDiscount =
            items?.prices?.discounts?.find((d) => d.type === 'membership')?.amount?.value ?? 0
          // tính grand total bao gồm cả membership discount
          const grandTotal = (items?.prices?.grand_total?.value ?? 0) + amountOfMembershipDiscount
          const orderAmount = items?.shipping_information?.order_amount ?? 0

          const shippingFee = grandTotal >= orderAmount ? 0 : shippingFeeValue
          // tính cashback coin
          // công thức là affiliate_information.cashback * affiliate_information.value / 100
          const cashbackCoin = Math.ceil(
            ((items?.affiliate_information?.cashback ?? 0) *
              (items?.prices?.subtotal_with_discount_excluding_tax.value ?? 0)) /
              100
          )

          set((state) => ({
            ...state,
            cartData: items ?? ({} as ICart),
            cartItems: items?.items ?? [],
            shippingFee: isHaveItems ? shippingFee : 0,
            isLoading: false,
            cashbackCoin: cashbackCoin,
          }))
          // return state loading
          return false
        },

        // ==================== CART ID MANAGEMENT ====================
        replaceCartId: async (cartId: string) => {
          get().clearCart()
          const token = await getCookieToken()
          set((state) => {
            state.cartId = cartId
            state.cartType = token ? TYPE_CART.USER : TYPE_CART.GUEST
            return state
          })
          await get().refetchCart()
        },

        // ==================== CART OPERATIONS ====================
        addToCart: async (items: ICartItem[], isToast?: boolean) => {
          if (!items.length) return

          try {
            await withPendingOperation(get, set, async () => {
              // Đảm bảo có cartId trước khi thêm
              if (!get().cartId) {
                await get().refetchCart()
              }

              const res = await addToCartAction(items, get()?.cartId)

              // Xử lý lỗi
              if (res?.user_errors?.length) {
                const errorMessage = res?.user_errors?.[0]?.message || res?.errors?.[0]?.message
                if (isToast) {
                  toast.error(errorMessage)
                } else {
                  throw new Error(errorMessage)
                }
              }

              // Hiển thị thông báo thành công
              if (isToast) {
                toast.success('Thêm vào giỏ hàng thành công')
              }
            })
          } catch (error) {
            // Nếu có lỗi thì không hiện cart mini
            console.error('Error in addToCart:', error)
            throw error
          }
        },

        // ==================== MERGE CART ====================
        mergeCart: async () => {
          //  if (get().cartType === TYPE_CART.USER) {
          //    await get().refetchCart()
          //  }

          //  return withPendingOperation(get, set, async () => {
          //    if (!get().cartId) {
          //      await get().creatCartId(await getCookieToken())
          //      return
          //    }

          //    const id = await mergeCartGuestToUser(get().cartId)
          //    if (id?.status === 'error') return

          //    set((state) => ({
          //      ...state,
          //      cartId: id,
          //      cartType: TYPE_CART.USER,
          //    }))

          //    await useWishlistStore.getState().getWishlist({})
          //  })
          if (get().cartType == TYPE_CART.USER) await get().refetchCart()
          set((state) => ({ ...state, isLoading: true }))
          if (!get().cartId) {
            return await get().refetchCart()
          }
          const id = await mergeCartGuestToUser(get().cartId)
          if (id?.status == 'error') return
          set((state) => {
            state.cartId = id
            state.cartType = TYPE_CART.USER
            return state
          })
          // await Promise.all([useWishlistStore.getState().getWishlist({}), get().refetchCart()])
          await get().refetchCart()
        },
        removeCartItem: async (cartItemUID, isRefetch = true) => {
          return withPendingOperation(
            get,
            set,
            async () => {
              const res = await removeCartItem(get()?.cartId, cartItemUID)
              if (res?.status === 'error') {
                throw new Error(res?.message)
              }
            },
            { shouldRefetch: isRefetch }
          )
        },
        uncheckAllItem: async (check?: boolean) => {
          if (!get().cartItems.length) return

          // Cập nhật UI ngay lập tức (optimistic update)
          set((state) => ({
            ...state,
            cartItems: state.cartItems.map((item) => ({
              ...item,
              available_to_checkout: check ? true : false,
            })),
          }))

          return withPendingOperation(
            get,
            set,
            async () => {
              const listItemUpdate = get().cartItems.map((item) => ({
                cart_item_uid: item.uid,
                quantity: item.quantity,
                available_to_checkout: check ? true : false,
              }))

              const res = await updateCartAll(get()?.cartId, listItemUpdate)
              if (res?.status === 'error') {
                toast.error(res?.message)
              }
            },
            { setLoading: false }
          )
        },
        updateCartItem: async (cartItemUID, quantity, available_to_checkout) => {
          return withPendingOperation(get, set, async () => {
            const res = await updateCart(get()?.cartId, {
              cart_item_uid: cartItemUID,
              quantity: quantity,
              //tạm thời chưa có tính năng, khi nào có tính năng thì mở ra
              // available_to_checkout: available_to_checkout,
            })

            handleErrorResponse(res)
          })
        },

        // ==================== CART CLEAR ====================
        clearCart: () => {
          set(() => defaultCart)
        },

        clearUserCart: () => {
          if (get().cartType === TYPE_CART.GUEST) return
          set((state) => {
            state.cartItems = []
            state.cartData = {} as ICart
            return state
          })
          get().refetchCart()
        },

        // ==================== COUPON OPERATIONS ====================
        addCoupon: async (coupon: string) => {
          return withPendingOperation(get, set, async () => {
            const res = await applyCoupon(get().cartId, coupon)

            if (res?.status === 'error') {
              handleErrorResponse(res)
              return
            }
          })
        },

        removeCoupon: async () => {
          return withPendingOperation(get, set, async () => {
            const res = await removeCouponService(get().cartId)

            if (res?.status === 'error') {
              handleErrorResponse(res)
              return
            }
          })
        },

        swapCoupon: async (newCoupon: string) => {
          return withPendingOperation(get, set, async () => {
            const { cartData } = get()

            // Remove existing coupon if any
            if (cartData?.applied_coupons && cartData?.applied_coupons.length > 0) {
              for (const item of cartData?.applied_coupons) {
                const removeRes = await removeCouponService(get().cartId)
                if (removeRes?.status === 'error') {
                  handleErrorResponse(removeRes, 'Không thể xóa mã giảm giá cũ')
                  return
                }
              }
            }

            // Add new coupon
            const addRes = await applyCoupon(get().cartId, newCoupon)
            if (addRes?.status === 'error') {
              handleErrorResponse(addRes, 'Không thể áp dụng mã giảm giá mới')
              return
            }
          })
        },
        // ==================== UTILITY ====================
        setLoading: (loading: boolean) => {
          set((state) => {
            state.isLoading = loading
            return state
          })
        },
        setShippingFee: (fee: number) => {
          // Set kiểu này thì nó mới render lại những chỗ dùng đến shippingFee
          set(() => ({ shippingFee: fee }))
        },
        setShowCartMini: (show: boolean) => {
          set(() => ({ showCartMini: show }))
        },
        setIsAutoCloseMiniCart: (isAuto: boolean) => {
          set(() => ({ isAutoCloseMiniCart: isAuto }))
        },
      }),
      {
        name: 'cart-storage',
        partialize: (state) => ({
          cartId: state.cartId,
          cartItems: state.cartItems,
          cartType: state.cartType,
          cartData: state.cartData,
        }),
      }
    )
  )
)
